import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import {
  getSubjects,
  createSubject,
  getCompetenceAims,
  createCompetenceAim,
  getCompetenceAimSkills,
  generateCompetenceAimSkills,
  saveCompetenceAimSkills,
  generateCompetenceAimKeyPoints,
  getClassCurriculum,
  setClassSubjects,
  setClassCompetenceAims,
  getClasses,
  createInteractiveLesson,
  createProgrammingLesson,
  createLesson
} from '../../services/api';
import './CompetenceAims.css';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import DarkModeToggle from '../../components/DarkModeToggle/DarkModeToggle';

const CompetenceAims = () => {
  const { user } = useAuth();
  const { handleRequest } = useApi();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // Subjects (fag) + kompetansemål
  const [subjects, setSubjects] = useState([]);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newSubjectCode, setNewSubjectCode] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [allCompetenceAims, setAllCompetenceAims] = useState([]);
  const [newCompetenceAimTitle, setNewCompetenceAimTitle] = useState('');
  const [newCompetenceAimCode, setNewCompetenceAimCode] = useState('');
  const [bulkCompetenceAims, setBulkCompetenceAims] = useState('');
  const [creatingBulk, setCreatingBulk] = useState(false);

  // Skills breakdown (delmål) for a competence aim - now per-aim
  const [expandedAimIds, setExpandedAimIds] = useState(new Set());
  const [aimSkillsBreakdowns, setAimSkillsBreakdowns] = useState({}); // { aimId: skillsBreakdown }
  const [skillsLanguage, setSkillsLanguage] = useState('no');
  const [savingSkills, setSavingSkills] = useState({}); // { aimId: boolean }
  const [generatingSkills, setGeneratingSkills] = useState({}); // { aimId: boolean }
  const [generatingKeyPointsFor, setGeneratingKeyPointsFor] = useState(null);
  
  // Selected delmål for lesson creation: Set of strings like "aimId:skillId"
  const [selectedDelmalIds, setSelectedDelmalIds] = useState(new Set());
  const [creatingLesson, setCreatingLesson] = useState(false);

  // Assign subjects/kompetansemål to a class
  const [classes, setClasses] = useState([]);
  const [curriculumClassId, setCurriculumClassId] = useState('');
  const [selectedClassSubjectIds, setSelectedClassSubjectIds] = useState([]);
  const [selectedClassCompetenceAimIds, setSelectedClassCompetenceAimIds] = useState([]);

  // Modal states
  const [showCreateSubjectModal, setShowCreateSubjectModal] = useState(false);
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [showAssignClassModal, setShowAssignClassModal] = useState(false);

  useEffect(() => {
    if (!user || (user.role !== 'teacher' && user.role !== 'admin' && user.role !== 'superadmin')) {
      navigate('/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const [subjectsResponse, competenceAimsResponse, classesResponse] = await Promise.all([
          handleRequest(getSubjects).catch(() => ({ data: [] })),
          handleRequest(getCompetenceAims).catch(() => ({ data: [] })),
          handleRequest(getClasses).catch(() => ({ data: [] }))
        ]);
        setSubjects(subjectsResponse.data || []);
        setAllCompetenceAims(competenceAimsResponse.data || []);
        setClasses(classesResponse.data || []);
      } catch (error) {
        setErrorMessage(error.message || 'Kunne ikke laste inn data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate, handleRequest]);

  // Default selected subject when subjects load
  useEffect(() => {
    if (!selectedSubjectId && subjects.length > 0) {
      setSelectedSubjectId(subjects[0]._id || subjects[0].id);
    }
  }, [subjects, selectedSubjectId]);


  // Load current curriculum selections for a class
  useEffect(() => {
    const loadCurriculum = async () => {
      try {
        if (!curriculumClassId) {
          setSelectedClassSubjectIds([]);
          setSelectedClassCompetenceAimIds([]);
          return;
        }
        const res = await handleRequest(getClassCurriculum, curriculumClassId);
        const classroom = res.data?.classroom;
        setSelectedClassSubjectIds((classroom?.subjectIds || []).map(String));
        setSelectedClassCompetenceAimIds((classroom?.competenceAimIds || []).map(String));
      } catch (error) {
        setErrorMessage(error.message || 'Kunne ikke laste inn fag/kompetansemål for klassen');
      }
    };
    loadCurriculum();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curriculumClassId]);

  const toggleIdInArray = (arr, id) => {
    const set = new Set(arr.map(String));
    if (set.has(String(id))) set.delete(String(id));
    else set.add(String(id));
    return Array.from(set);
  };

  // Load skills breakdown when a competence aim is expanded
  const loadSkillsForAim = async (aimId) => {
    try {
      if (aimSkillsBreakdowns[aimId]) return; // Already loaded
      const res = await handleRequest(getCompetenceAimSkills, aimId);
      setAimSkillsBreakdowns((prev) => ({
        ...prev,
        [aimId]: res.data?.skillsBreakdown || { items: [] }
      }));
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke laste inn delmål for kompetansemålet');
      setAimSkillsBreakdowns((prev) => ({
        ...prev,
        [aimId]: { items: [] }
      }));
    }
  };

  const toggleAimExpanded = (aimId) => {
    const newExpanded = new Set(expandedAimIds);
    if (newExpanded.has(aimId)) {
      newExpanded.delete(aimId);
    } else {
      newExpanded.add(aimId);
      loadSkillsForAim(aimId);
    }
    setExpandedAimIds(newExpanded);
  };

  const handleCreateSubject = async (e) => {
    e.preventDefault();
    try {
      if (!newSubjectName.trim()) {
        setErrorMessage('Skriv inn navn på fag');
        return;
      }
      const res = await handleRequest(createSubject, { 
        name: newSubjectName.trim(),
        code: newSubjectCode.trim() || undefined
      });
      setSubjects((prev) => {
        const next = [res.data, ...prev];
        next.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'nb'));
        return next;
      });
      setNewSubjectName('');
      setNewSubjectCode('');
      setErrorMessage('');
      setShowCreateSubjectModal(false);
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke opprette fag');
    }
  };

  const handleCreateCompetenceAim = async (e) => {
    e.preventDefault();
    try {
      if (!selectedSubjectId) {
        setErrorMessage('Velg et fag først');
        return;
      }
      if (!newCompetenceAimTitle.trim()) {
        setErrorMessage('Skriv inn kompetansemål');
        return;
      }
      const res = await handleRequest(createCompetenceAim, {
        subjectId: selectedSubjectId,
        title: newCompetenceAimTitle.trim(),
        code: newCompetenceAimCode.trim() || undefined
      });
      setAllCompetenceAims((prev) => [res.data, ...prev]);
      setNewCompetenceAimTitle('');
      setNewCompetenceAimCode('');
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke opprette kompetansemål');
    }
  };

  const handleBulkCreateCompetenceAims = async (e) => {
    e.preventDefault();
    try {
      if (!selectedSubjectId) {
        setErrorMessage('Velg et fag først');
        return;
      }
      if (!bulkCompetenceAims.trim()) {
        setErrorMessage('Skriv inn kompetansemål');
        return;
      }

      setCreatingBulk(true);
      setErrorMessage('');

      // Parse lines - each line can be:
      // - Just a title: "Kompetansemål tekst"
      // - Code and title separated by dash: "MAT01-05 – Kompetansemål tekst"
      // - Code and title separated by space: "MAT01-05 Kompetansemål tekst"
      const lines = bulkCompetenceAims
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      const createdAims = [];
      const errors = [];

      for (const line of lines) {
        try {
          let code = null;
          let title = line;

          // Try to parse code from the line
          // Pattern 1: "CODE – Title" (with em dash or regular dash)
          const dashMatch = line.match(/^([A-Z0-9\-]+)\s*[–-]\s*(.+)$/);
          if (dashMatch) {
            code = dashMatch[1].trim();
            title = dashMatch[2].trim();
          } else {
            // Pattern 2: "CODE Title" (space separated, code is alphanumeric with dashes)
            const spaceMatch = line.match(/^([A-Z0-9\-]{2,})\s+(.+)$/);
            if (spaceMatch) {
              code = spaceMatch[1].trim();
              title = spaceMatch[2].trim();
            }
          }

          if (!title) {
            errors.push(`Tom linje eller ugyldig format: "${line}"`);
            continue;
          }

          const res = await handleRequest(createCompetenceAim, {
            subjectId: selectedSubjectId,
            title: title,
            code: code || undefined
          });
          createdAims.push(res.data);
        } catch (error) {
          errors.push(`Kunne ikke opprette "${line}": ${error.message || 'Ukjent feil'}`);
        }
      }

      if (createdAims.length > 0) {
        setAllCompetenceAims((prev) => [...createdAims, ...prev]);
      }

      if (errors.length > 0) {
        setErrorMessage(`Opprettet ${createdAims.length} kompetansemål. Feil: ${errors.join('; ')}`);
      } else {
        setErrorMessage('');
      }

      setBulkCompetenceAims('');
      setShowBulkCreateModal(false);
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke opprette kompetansemål');
    } finally {
      setCreatingBulk(false);
    }
  };

  const handleSaveClassCurriculum = async (e) => {
    e.preventDefault();
    try {
      if (!curriculumClassId) {
        setErrorMessage('Velg en klasse');
        return;
      }
      await handleRequest(setClassSubjects, curriculumClassId, selectedClassSubjectIds);
      await handleRequest(setClassCompetenceAims, curriculumClassId, selectedClassCompetenceAimIds);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke lagre fag/kompetansemål for klassen');
    }
  };

  const handleGenerateSkillsBreakdown = async (aimId) => {
    try {
      setGeneratingSkills((prev) => ({ ...prev, [aimId]: true }));
      setErrorMessage('');
      const res = await handleRequest(generateCompetenceAimSkills, aimId, {
        language: skillsLanguage,
        maxSkills: 18,
        append: false,
        teacherSuggestions: []
      });
      setAimSkillsBreakdowns((prev) => ({
        ...prev,
        [aimId]: res.data?.skillsBreakdown || { items: [] }
      }));
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke generere delmål');
    } finally {
      setGeneratingSkills((prev) => ({ ...prev, [aimId]: false }));
    }
  };

  const handleSaveSkills = async (aimId) => {
    try {
      const skillsBreakdown = aimSkillsBreakdowns[aimId];
      if (!skillsBreakdown) return;
      setSavingSkills((prev) => ({ ...prev, [aimId]: true }));
      await handleRequest(saveCompetenceAimSkills, aimId, skillsBreakdown?.items || []);
      const refreshed = await handleRequest(getCompetenceAimSkills, aimId);
      setAimSkillsBreakdowns((prev) => ({
        ...prev,
        [aimId]: refreshed.data?.skillsBreakdown || { items: [] }
      }));
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke lagre delmål');
    } finally {
      setSavingSkills((prev) => ({ ...prev, [aimId]: false }));
    }
  };

  const updateSkillItem = (aimId, skillId, patch) => {
    setAimSkillsBreakdowns((prev) => {
      const breakdown = prev[aimId] || { items: [] };
      const items = (breakdown.items || []).map((it) => (String(it.id) === String(skillId) ? { ...it, ...patch } : it));
      return {
        ...prev,
        [aimId]: { ...breakdown, items }
      };
    });
  };

  const deleteSkillItem = async (aimId, skillId) => {
    try {
      const breakdown = aimSkillsBreakdowns[aimId];
      if (!breakdown) return;
      
      // Remove from selection if selected
      const delmalKey = `${aimId}:${skillId}`;
      setSelectedDelmalIds((prev) => {
        const next = new Set(prev);
        next.delete(delmalKey);
        return next;
      });
      
      // Remove from local state first
      const updatedItems = (breakdown.items || []).filter((it) => String(it.id) !== String(skillId));
      setAimSkillsBreakdowns((prev) => ({
        ...prev,
        [aimId]: { ...breakdown, items: updatedItems }
      }));
      
      // Save to backend
      await handleRequest(saveCompetenceAimSkills, aimId, updatedItems);
      
      // Reload to ensure we have the latest state
      const refreshed = await handleRequest(getCompetenceAimSkills, aimId);
      setAimSkillsBreakdowns((prev) => ({
        ...prev,
        [aimId]: refreshed.data?.skillsBreakdown || { items: [] }
      }));
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke slette delmål');
      // Reload to restore previous state on error
      const refreshed = await handleRequest(getCompetenceAimSkills, aimId);
      setAimSkillsBreakdowns((prev) => ({
        ...prev,
        [aimId]: refreshed.data?.skillsBreakdown || { items: [] }
      }));
    }
  };

  const handleSaveDescription = async (aimId, skillId) => {
    try {
      const breakdown = aimSkillsBreakdowns[aimId];
      if (!breakdown) return;
      // Save the current skills breakdown to backend
      await handleRequest(saveCompetenceAimSkills, aimId, breakdown?.items || []);
      // Optionally reload to ensure consistency
      const refreshed = await handleRequest(getCompetenceAimSkills, aimId);
      setAimSkillsBreakdowns((prev) => ({
        ...prev,
        [aimId]: refreshed.data?.skillsBreakdown || { items: [] }
      }));
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke lagre beskrivelse');
    }
  };

  const handleGenerateKeyPoints = async (aimId, skillId) => {
    try {
      setGeneratingKeyPointsFor(skillId);
      setErrorMessage('');
      
      const breakdown = aimSkillsBreakdowns[aimId];
      // First, save the current skills breakdown to ensure all skills are persisted with their IDs
      if (breakdown?.items?.length) {
        try {
          await handleRequest(saveCompetenceAimSkills, aimId, breakdown.items);
        } catch (saveError) {
          console.warn('Failed to save skills before generating key points:', saveError);
          // Continue anyway - the skill might already be saved
        }
      }
      
      // Find the skill to get its description
      const skill = breakdown?.items?.find((s) => String(s.id) === String(skillId));
      if (!skill) {
        setErrorMessage(`Delmål ikke funnet i lokal state. ID: ${skillId}`);
        return;
      }
      
      const res = await handleRequest(generateCompetenceAimKeyPoints, aimId, String(skillId), {
        language: skillsLanguage,
        focus: undefined,
        description: skill.description || undefined
      });
      // Reload the skills breakdown to get the updated data from the server
      const refreshed = await handleRequest(getCompetenceAimSkills, aimId);
      setAimSkillsBreakdowns((prev) => ({
        ...prev,
        [aimId]: refreshed.data?.skillsBreakdown || { items: [] }
      }));
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke generere nøkkelpunkter');
    } finally {
      setGeneratingKeyPointsFor(null);
    }
  };

  // Toggle delmål selection
  const toggleDelmalSelection = (aimId, skillId) => {
    const key = `${aimId}:${skillId}`;
    setSelectedDelmalIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Get selected delmål data
  const getSelectedDelmalData = () => {
    const selected = [];
    selectedDelmalIds.forEach((key) => {
      const [aimId, skillId] = key.split(':');
      const breakdown = aimSkillsBreakdowns[aimId];
      if (breakdown?.items) {
        const skill = breakdown.items.find((s) => String(s.id) === String(skillId));
        const aim = allCompetenceAims.find((a) => String(a._id || a.id) === String(aimId));
        if (skill && aim) {
          selected.push({ aimId, skillId, skill, aim });
        }
      }
    });
    return selected;
  };

  // Create lesson from selected delmål
  const handleCreateLessonFromDelmal = async (lessonType) => {
    const selected = getSelectedDelmalData();
    if (selected.length === 0) {
      setErrorMessage('Velg minst ett delmål');
      return;
    }

    setCreatingLesson(true);
    setErrorMessage('');

    try {
      if (lessonType === 'interactive') {
        // For interactive lessons, create one lesson per selected delmål
        // Or create one lesson with all delmål - let's do one per delmål for now
        for (const { aimId, skillId, skill } of selected) {
          const formData = {
            competenceAimId: aimId,
            competenceAimSkillId: skillId,
            topic: skill.title || skill.description || '',
            subjectArea: 'programming', // Default, could be made configurable
            learnerProfile: '16–18 year old high school students',
            durationMinutes: 30,
            coursePosition: 'First introduction to the topic',
            lessonLanguage: 'no',
            level: 'basic'
          };
          const response = await handleRequest(createInteractiveLesson, formData);
          navigate(`/interactive-lessons/${response.data._id || response.data.id}/edit`);
          break; // Navigate after first one, user can create more if needed
        }
      } else if (lessonType === 'programming') {
        // For programming lessons, use delmål title/description as content
        for (const { aimId, skillId, skill } of selected) {
          const content = `${skill.title || ''}\n\n${skill.description || ''}`.trim();
          const formData = {
            title: skill.title || 'Programmeringsoppgave',
            description: skill.description || '',
            language: 'javascript',
            level: 'basic',
            content: content || skill.title || 'Programmeringsoppgave',
            taskCount: 3,
            lessonLanguage: 'no'
          };
          const response = await handleRequest(createProgrammingLesson, formData);
          navigate(`/programming-lessons/${response.data._id || response.data.id}/edit`);
          break; // Navigate after first one
        }
      } else if (lessonType === 'quiz') {
        // For quiz lessons, use delmål title/description as content
        for (const { aimId, skillId, skill } of selected) {
          const content = `${skill.title || ''}\n\n${skill.description || ''}`.trim();
          const formData = {
            title: skill.title || 'Quiz',
            content: content || skill.title || 'Quiz',
            questionCount: 5,
            quizTypes: ['multiple-choice'],
            lessonLanguage: 'no'
          };
          const response = await handleRequest(createLesson, formData);
          navigate(`/lessons/${response.data._id || response.data.id}/edit`);
          break; // Navigate after first one
        }
      }
    } catch (error) {
      setErrorMessage(error.message || `Kunne ikke opprette ${lessonType === 'interactive' ? 'interaktiv' : lessonType === 'programming' ? 'programmerings' : 'quiz'} leksjon`);
    } finally {
      setCreatingLesson(false);
    }
  };


  if (loading) {
    return (
      <div className="competence-aims-container">
        <div className="loading">Laster...</div>
      </div>
    );
  }

  const competenceAimsForSelectedSubject = selectedSubjectId
    ? allCompetenceAims.filter((a) => String(a?.subjectId?._id || a?.subjectId) === String(selectedSubjectId))
    : [];
  const competenceAimsForAssignment = (selectedClassSubjectIds || []).length > 0
    ? allCompetenceAims.filter((a) => (selectedClassSubjectIds || []).map(String).includes(String(a?.subjectId?._id || a?.subjectId)))
    : allCompetenceAims;

  return (
    <div className="competence-aims-container">
      <div className="competence-aims-header">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          ← Tilbake til oversikten
        </button>
        <h1>Fag og kompetansemål</h1>
        <DarkModeToggle />
      </div>

      {errorMessage && <ErrorCard message={errorMessage} />}

      <div className="competence-aims-content">
        <div className="dashboard-card">
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '2rem' }}>
            <button 
              className="create-lesson-btn" 
              onClick={() => setShowCreateSubjectModal(true)}
            >
              + Opprett fag
            </button>
            <button 
              className="create-lesson-btn" 
              onClick={() => setShowBulkCreateModal(true)}
            >
              + Lim inn flere kompetansemål (bulk)
            </button>
            <button 
              className="create-lesson-btn" 
              onClick={() => setShowAssignClassModal(true)}
            >
              + Tildel fag og kompetansemål til klasse
            </button>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <h3>Kompetansemål i valgt fag</h3>
            <div className="form-group" style={{ marginBottom: '1rem', maxWidth: '400px' }}>
              <label>Velg fag</label>
              <select
                value={selectedSubjectId}
                onChange={(e) => setSelectedSubjectId(e.target.value)}
              >
                <option value="">Velg et fag…</option>
                {subjects.map((s) => (
                  <option key={s._id || s.id} value={s._id || s.id}>
                    {s.code ? `${s.code} – ` : ''}{s.name}
                  </option>
                ))}
              </select>
            </div>
            {selectedSubjectId ? (
              competenceAimsForSelectedSubject.length > 0 ? (
                <div className="competence-aims-accordion">
                  {competenceAimsForSelectedSubject.map((a) => {
                    const aimId = a._id || a.id;
                    const isExpanded = expandedAimIds.has(aimId);
                    const skillsBreakdown = aimSkillsBreakdowns[aimId] || { items: [] };
                    const hasSkills = skillsBreakdown.items && skillsBreakdown.items.length > 0;
                    
                    return (
                      <div key={aimId} className="accordion-item">
                        <div 
                          className="accordion-header"
                          onClick={() => toggleAimExpanded(aimId)}
                        >
                          <div className="accordion-title">
                            <span className="accordion-icon">{isExpanded ? '▼' : '▶'}</span>
                            <strong>{a.code ? `${a.code} – ` : ''}{a.title}</strong>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="accordion-content">
                            {!hasSkills ? (
                              <div style={{ padding: '1rem', textAlign: 'center' }}>
                                <p style={{ marginBottom: '1rem' }}>Ingen delmål generert ennå.</p>
                                <button
                                  className="create-lesson-btn"
                                  onClick={() => handleGenerateSkillsBreakdown(aimId)}
                                  disabled={generatingSkills[aimId]}
                                >
                                  {generatingSkills[aimId] ? 'Genererer...' : 'Generer delmål'}
                                </button>
                              </div>
                            ) : (
                              <div>
                                {selectedDelmalIds.size > 0 && (
                                  <div style={{ 
                                    display: 'flex', 
                                    gap: '0.75rem', 
                                    marginBottom: '1rem', 
                                    flexWrap: 'wrap',
                                    padding: '1rem',
                                    backgroundColor: 'var(--card-bg, #f5f5f5)',
                                    borderRadius: '8px',
                                    border: '2px solid var(--primary-color, #007bff)'
                                  }}>
                                    <div style={{ flex: 1, minWidth: '200px' }}>
                                      <strong>{selectedDelmalIds.size} delmål valgt</strong>
                                    </div>
                                    <button
                                      className="create-lesson-btn"
                                      onClick={() => handleCreateLessonFromDelmal('interactive')}
                                      disabled={creatingLesson}
                                      style={{ flex: '0 0 auto' }}
                                    >
                                      {creatingLesson ? 'Oppretter...' : 'Opprett interaktiv leksjon'}
                                    </button>
                                    <button
                                      className="create-lesson-btn"
                                      onClick={() => handleCreateLessonFromDelmal('programming')}
                                      disabled={creatingLesson}
                                      style={{ flex: '0 0 auto' }}
                                    >
                                      {creatingLesson ? 'Oppretter...' : 'Opprett programmeringsleksjon'}
                                    </button>
                                    <button
                                      className="create-lesson-btn"
                                      onClick={() => handleCreateLessonFromDelmal('quiz')}
                                      disabled={creatingLesson}
                                      style={{ flex: '0 0 auto' }}
                                    >
                                      {creatingLesson ? 'Oppretter...' : 'Opprett quiz'}
                                    </button>
                                  </div>
                                )}
                                <ul className="simple-list">
                                  {skillsBreakdown.items.map((s, idx) => {
                                    const delmalKey = `${aimId}:${s.id}`;
                                    const isSelected = selectedDelmalIds.has(delmalKey);
                                    return (
                                      <li key={s.id || `${idx}-${s.title}`}>
                                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                                          <div style={{ flex: '0 0 auto', marginTop: '0.5rem' }}>
                                            <input
                                              type="checkbox"
                                              id={`delmal-${delmalKey}`}
                                              checked={isSelected}
                                              onChange={() => toggleDelmalSelection(aimId, s.id)}
                                              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                            />
                                          </div>
                                          <div style={{ flex: 1 }}>
                                            <label 
                                              htmlFor={`delmal-${delmalKey}`}
                                              style={{ 
                                                display: 'block', 
                                                fontWeight: isSelected ? 'bold' : 'normal',
                                                cursor: 'pointer',
                                                marginBottom: '0.5rem'
                                              }}
                                            >
                                              {s.title || `Delmål ${idx + 1}`}
                                            </label>
                                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                                              <button
                                                type="button"
                                                className="delete-btn"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  deleteSkillItem(aimId, s.id);
                                                }}
                                              >
                                                Fjern
                                              </button>
                                            </div>
                                            <div style={{ marginTop: 8 }}>
                                              <label>Beskrivelse</label>
                                              <textarea
                                                value={s.description || ''}
                                                onChange={(e) => updateSkillItem(aimId, s.id, { description: e.target.value })}
                                                onBlur={() => handleSaveDescription(aimId, s.id)}
                                                rows={1}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="no-lessons">Ingen kompetansemål i dette faget ennå.</p>
              )
            ) : (
              <p className="no-lessons">Velg et fag for å se kompetansemål.</p>
            )}
          </div>

          {/* Modals */}
          {showCreateSubjectModal && (
            <div className="modal-overlay" onClick={() => setShowCreateSubjectModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Opprett fag</h2>
                  <button className="modal-close" onClick={() => setShowCreateSubjectModal(false)}>×</button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleCreateSubject}>
                    <div className="form-group">
                      <label>Navn</label>
                      <input
                        type="text"
                        value={newSubjectName}
                        onChange={(e) => setNewSubjectName(e.target.value)}
                        placeholder="F.eks. Matematikk"
                      />
                    </div>
                    <div className="form-group">
                      <label>Kode (valgfritt)</label>
                      <input
                        type="text"
                        value={newSubjectCode}
                        onChange={(e) => setNewSubjectCode(e.target.value)}
                        placeholder="F.eks. MAT, ENG, NO"
                      />
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="cancel-btn" onClick={() => setShowCreateSubjectModal(false)}>
                        Avbryt
                      </button>
                      <button type="submit" className="create-lesson-btn">
                        Opprett fag
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {showBulkCreateModal && (
            <div className="modal-overlay" onClick={() => setShowBulkCreateModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Lim inn flere kompetansemål (bulk)</h2>
                  <button className="modal-close" onClick={() => setShowBulkCreateModal(false)}>×</button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleBulkCreateCompetenceAims}>
                    <div className="form-group">
                      <label>Fag</label>
                      <select
                        value={selectedSubjectId}
                        onChange={(e) => setSelectedSubjectId(e.target.value)}
                      >
                        <option value="">Velg…</option>
                        {subjects.map((s) => (
                          <option key={s._id || s.id} value={s._id || s.id}>
                            {s.code ? `${s.code} – ` : ''}{s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Kompetansemål (én per linje)</label>
                      <textarea
                        value={bulkCompetenceAims}
                        onChange={(e) => setBulkCompetenceAims(e.target.value)}
                        placeholder={`Lim inn kompetansemål, én per linje.

Format:
MAT01-05 – Kompetansemål tekst
eller bare:
Kompetansemål tekst`}
                        rows={10}
                        style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                      />
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="cancel-btn" onClick={() => setShowBulkCreateModal(false)}>
                        Avbryt
                      </button>
                      <button 
                        type="submit" 
                        className="create-lesson-btn"
                        disabled={!selectedSubjectId || !bulkCompetenceAims.trim() || creatingBulk}
                      >
                        {creatingBulk ? 'Oppretter...' : 'Opprett alle kompetansemål'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {showAssignClassModal && (
            <div className="modal-overlay" onClick={() => setShowAssignClassModal(false)}>
              <div className="modal-content modal-content-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Tildel fag og kompetansemål til klasse</h2>
                  <button className="modal-close" onClick={() => setShowAssignClassModal(false)}>×</button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSaveClassCurriculum}>
              <div className="form-group" style={{ maxWidth: 420 }}>
                <label>Klasse</label>
                <select
                  value={curriculumClassId}
                  onChange={(e) => setCurriculumClassId(e.target.value)}
                >
                  <option value="">Velg…</option>
                  {classes.map((c) => (
                    <option key={c._id || c.id} value={c._id || c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group half-width">
                  <label>Fag</label>
                  {subjects.length > 0 ? (
                    <div className="checkbox-group">
                      {subjects.map((s) => {
                        const id = s._id || s.id;
                        const checked = (selectedClassSubjectIds || []).map(String).includes(String(id));
                        return (
                          <div key={id} className="checkbox-item">
                            <input
                              type="checkbox"
                              id={`class-subject-${id}`}
                              checked={checked}
                              onChange={() => setSelectedClassSubjectIds((prev) => toggleIdInArray(prev || [], id))}
                              disabled={!curriculumClassId}
                            />
                            <label htmlFor={`class-subject-${id}`}>
                              {s.code ? `${s.code} – ` : ''}{s.name}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="no-lessons">Opprett et fag først.</p>
                  )}
                </div>

                <div className="form-group half-width">
                  <label>Kompetansemål</label>
                  {competenceAimsForAssignment.length > 0 ? (
                    <div className="checkbox-group" style={{ maxHeight: 220, overflowY: 'auto' }}>
                      {competenceAimsForAssignment.map((a) => {
                        const id = a._id || a.id;
                        const checked = (selectedClassCompetenceAimIds || []).map(String).includes(String(id));
                        const subjectName = a?.subjectId?.name ? ` (${a.subjectId.name})` : '';
                        return (
                          <div key={id} className="checkbox-item">
                            <input
                              type="checkbox"
                              id={`class-aim-${id}`}
                              checked={checked}
                              onChange={() => setSelectedClassCompetenceAimIds((prev) => toggleIdInArray(prev || [], id))}
                              disabled={!curriculumClassId}
                            />
                            <label htmlFor={`class-aim-${id}`}>
                              {a.code ? `${a.code} – ` : ''}{a.title}{subjectName}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="no-lessons">Ingen kompetansemål tilgjengelig.</p>
                  )}
                </div>
              </div>

                    <div className="modal-actions">
                      <button type="button" className="cancel-btn" onClick={() => setShowAssignClassModal(false)}>
                        Avbryt
                      </button>
                      <button type="submit" className="create-lesson-btn" disabled={!curriculumClassId}>
                        Lagre for klasse
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default CompetenceAims;
